/**
 * Registry central de query keys.
 *
 * Mantener las keys aquí (en vez de strings sueltos) tiene tres ventajas:
 *   · Renombrar es fácil · sin grep de strings mágicos.
 *   · `qc.invalidateQueries({ queryKey: qk.prorrateo(id) })` queda tipado.
 *   · Garantiza unicidad · ningún feature inventa una key que choque con otra.
 *
 * Convención: la primera entrada del array es el "namespace" (solicitud,
 * prorrateo, catalogos…), las siguientes son los discriminadores.
 */
export const qk = {
  solicitud: (id: string) => ["solicitud", id] as const,
  ventanasPuePpd: (id: string) => ["ventanas-pue-ppd", id] as const,
  prorrateo: (id: string) => ["prorrateo", id] as const,
  prorrateos: (id: string) => ["prorrateos", id] as const,
  proveedoresAgrupados: (id: string) => ["proveedores-agrupados", id] as const,
  documentos: (id: string) => ["documentos", id] as const,
  polizas: (id: string) => ["polizas", id] as const,
  cecos: ["catalogos", "cecos"] as const,
  cuentas: ["catalogos", "cuentas"] as const,
  clavesSat: ["catalogos", "claves-sat"] as const,
  areas: ["catalogos", "areas"] as const,
  tiposDocumento: ["catalogos", "tipos-documento"] as const,
  indicadoresImpuesto: ["catalogos", "indicadores-impuesto"] as const,
  misSolicitudes: ["mis-solicitudes"] as const,
  seguridad2FA: ["seguridad", "2fa"] as const,
  miPerfil: ["perfil", "mi-perfil"] as const,
  misAsignaciones: ["perfil", "asignaciones"] as const,
  misFacturas: (filtros: object = {}) => ["mis-facturas", filtros] as const,
  misEstadisticas: ["mis-estadisticas"] as const,
  adminAreas: ["admin", "areas"] as const,
  satDefault: ["admin", "sat-mock", "default"] as const,
  satOverrides: ["admin", "sat-mock", "overrides"] as const,
  bandejaAutorizacion: ["autorizacion", "bandeja"] as const,
  historialAutorizaciones: ["autorizacion", "historial"] as const,
  adminOrganizaciones: ["admin", "organizaciones"] as const,
  adminFlujosAutorizacion: ["admin", "flujos-autorizacion"] as const,
  adminProveedores: ["admin", "proveedores"] as const,
  adminUsuarios: ["admin", "usuarios"] as const,
  adminRoles: ["admin", "roles"] as const,
  adminPermisos: ["admin", "permisos"] as const,
  adminPermisosGestion: ["admin", "permisos", "gestion"] as const,
  adminCategoriasDocumento: ["admin", "categorias-documento"] as const,
  adminEquivalenciasUnidad: ["admin", "equivalencias-unidad"] as const,
  adminIndicadoresImpuesto: ["admin", "indicadores-impuesto"] as const,
  adminMapeosClaveCuenta: ["admin", "mapeos-clave-cuenta"] as const,
  adminTiposDocumento: ["admin", "tipos-documento"] as const,
  adminConfiguraciones: ["admin", "configuraciones"] as const,
  adminOrdenesCompra: ["admin", "ordenes-compra"] as const,
  adminOrdenCompra: (clave: string) => ["admin", "ordenes-compra", clave] as const,
  adminContratosAnualizados: ["admin", "contratos-anualizados"] as const,
  adminContratoAnualizado: (clave: string) => ["admin", "contratos-anualizados", clave] as const,
  auditoriaDocumentos: ["admin", "auditoria-documentos"] as const,
  auditoriaCambios: ["admin", "auditoria-cambios"] as const,
  solicitudCatalogoEstado: (tipo: string, clave: string) =>
    ["solicitud-catalogo", "estado", tipo, clave] as const,
  misSolicitudesCatalogo: ["solicitud-catalogo", "mias"] as const,
  misNotificacionesCatalogo: ["solicitud-catalogo", "mias", "no-vistas"] as const,
  misNotificaciones: ["notificaciones", "mias"] as const,
  misNotificacionesCount: ["notificaciones", "mias", "no-vistas"] as const,
  adminSolicitudesCatalogo: ["admin", "solicitudes-catalogo"] as const,
  solicitudesCatalogoPendientes: ["admin", "solicitudes-catalogo", "pendientes"] as const,
  calendarioPagos: (filtros: object = {}) => ["calendario-pagos", filtros] as const,
  historicoFiltros: ["historico", "filtros"] as const,
  historicoBusqueda: (filtros: object) => ["historico", "busqueda", filtros] as const,
  chat: (id: string) => ["chat", id] as const,
  historialSolicitud: (id: string) => ["historial-solicitud", id] as const,
  // P44 · FB60 Contrapago
  misOrganizaciones: ["catalogos", "organizaciones"] as const,
  proveedoresBuscar: (params: object) => ["proveedores-buscar", params] as const,
  enEsperaCfdi: (params: object) => ["en-espera-cfdi", params] as const,
  // Reportería (builder de usuario · S11)
  repCatalogo: ["rep", "catalogo"] as const,
  repRelaciones: ["rep", "relaciones"] as const,
  repTemplates: ["rep", "templates"] as const,
  repColumnas: ["rep", "columnas-personalizadas"] as const,
  repPredefinidos: ["rep", "predefinidos"] as const,
  repTrabajos: ["rep", "trabajos"] as const,
  repValores: (token: string) => ["rep", "valores", token] as const,
  repTotal: (firma: string) => ["rep", "total", firma] as const,
  repPreview: (firma: string) => ["rep", "preview", firma] as const,
  // Reportería · administración de configuraciones
  repAdminConfigs: ["rep", "admin", "configuraciones"] as const,
  repAdminConfig: (id: number) => ["rep", "admin", "configuracion", id] as const,
  repAdminTablas: ["rep", "admin", "introspeccion", "tablas"] as const,
  repAdminColumnas: (esquema: string, tabla: string) =>
    ["rep", "admin", "introspeccion", "columnas", esquema, tabla] as const,
  repAdminRoles: ["rep", "admin", "roles"] as const,
  repAdminUsuarios: ["rep", "admin", "usuarios"] as const,
  // Reportería · builder de predefinidos en contexto de config
  repAdminDefCatalogo: (idConfig: number) => ["rep", "admin", "def-catalogo", idConfig] as const,
  repAdminDefRelaciones: (idConfig: number) => ["rep", "admin", "def-relaciones", idConfig] as const,
  repAdminPredefinidos: (idConfig: number) => ["rep", "admin", "predefinidos", idConfig] as const,
  repAdminDefPreview: (idConfig: number, firma: string) => ["rep", "admin", "def-preview", idConfig, firma] as const,
  repAdminDefTotal: (idConfig: number, firma: string) => ["rep", "admin", "def-total", idConfig, firma] as const,
} as const;
